import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Outlet } from "react-router";
import { AdminOnlyNotice } from "@/components/AdminOnlyNotice";
import { ReadFailed } from "@/components/ReadFailed";
import { beginMeRequest, settledMeRequest, settleUserRole, useUserRole, type UserRoleInfo } from "@/hooks/use-rbac";
import { apiFetch } from "@/lib/api";

/**
 * #4733: what AppShell renders in place of the page on an ADMIN_ROUTE_PAGES
 * route (matchesAdminRoute). Every read such a page makes is admin-only on the
 * server, so the page mounts only for an admin the server will answer:
 *  - /api/rbac/me is read again on entry (a cached is_admin can outlive a
 *    demotion by the query's ten-minute staleTime) and the page waits for it;
 *  - a station PIN session gets the refusal: the server refuses it every
 *    /api/admin read (isPinSessionRefusedPath), admin or not;
 *  - a failed /me read says so with a retry, never a blank well and never the
 *    page on cached data;
 *  - so does one that has not answered in ADMIN_ROUTE_CHECK_MS (round 4): a
 *    hung or paused request is not left as a spinner with no way on.
 *
 * #4922: each check is its own query (a key per mount and per Retry), never
 * the shared ["rbac","me"] one. A shared fetch is deduplicated, so a gate
 * mounted by navigation joined a /me begun before it (perhaps before a
 * demotion) and accepted its answer; and a Retry with nothing cached joined
 * the hung request instead of sending a new one. A new key always sends a new
 * request under a new limit, and the check left behind is cancelled through
 * its AbortSignal. A fresh answer also refreshes ["rbac","me"] for the shell.
 *
 * Round 2: that refresh first cancels the shell's own /me in flight, then
 * settles by request number (settleUserRole), so a shell answer sent before
 * the check cannot land after it and undo it, in the shared copy or the
 * warm-load cache. Only the current check writes.
 *
 * A refusal is not final: it offers Check again, and a /me sent after the
 * check that settles as admin (the shell's, after a promotion) starts a new
 * check. The page still waits for that check's own answer.
 */
export const ADMIN_ROUTE_CHECK_MS = 8_000;

let lastCheck = 0;
const nextCheck = () => ++lastCheck;

export function AdminRouteGate() {
  const queryClient = useQueryClient();
  const [check, setCheck] = useState(nextCheck);
  const [timedOut, setTimedOut] = useState(false);
  /** The /me request number each check sent. */
  const sentAs = useRef(new Map<number, number>());
  const { data: shell } = useUserRole();
  const { data, error, isError } = useQuery({
    queryKey: ["rbac", "me", "admin-route-check", check],
    queryFn: async ({ signal }) => {
      const request = beginMeRequest();
      sentAs.current.set(check, request);
      const me = await apiFetch<UserRoleInfo>("/api/rbac/me", undefined, signal);
      if (!signal.aborted && check === lastCheck) {
        await queryClient.cancelQueries({ queryKey: ["rbac", "me"], exact: true });
        queryClient.setQueryData(["rbac", "me"], settleUserRole(request, me));
      }
      return me;
    },
    // One answer per check: a new check is a new key, never a refetch.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const waiting = !data && !isError;
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setTimedOut(true), ADMIN_ROUTE_CHECK_MS);
    return () => clearTimeout(timer);
  }, [waiting, check]);
  const retry = () => {
    setTimedOut(false);
    setCheck(nextCheck());
  };
  const refused = !!data && (!data.is_admin || !!data.pin_session);
  const promoted =
    refused && !!shell?.is_admin && !shell.pin_session && settledMeRequest() > (sentAs.current.get(check) ?? Infinity);
  useEffect(() => {
    if (promoted) retry();
  }, [promoted]);
  if (isError) return <ReadFailed what="your permissions" error={error} onRetry={retry} />;
  if (waiting && timedOut) {
    return <ReadFailed what="your permissions" error={null} reason="The check is taking too long." onRetry={retry} />;
  }
  if (!data) {
    return (
      <div role="status" className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Checking your access
      </div>
    );
  }
  if (refused) return <AdminOnlyNotice station={data.is_admin && !!data.pin_session} onRecheck={retry} />;
  return <Outlet />;
}
